import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTrash, faPlus, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * Allowed message types used by MessageAlert and its context helpers.
 * These map to visual configurations defined in TYPE_CONFIG.
 */
const MESSAGE_TYPES = {
  SUCCESS: 'success',
  DELETE: 'delete',
  CREATE: 'create',
  UPDATE: 'update',
  ERROR: 'error',
};

const TYPE_CONFIG = {
  [MESSAGE_TYPES.SUCCESS]: {
    icon: faCheck,
    color: UI_COLORS.STATUS.SUCCESS,
    bgColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.2),
    borderColor: UI_COLORS.STATUS.SUCCESS,
  },
  [MESSAGE_TYPES.DELETE]: {
    icon: faTrash,
    color: UI_COLORS.STATUS.ERROR,
    bgColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.2),
    borderColor: UI_COLORS.STATUS.ERROR,
  },
  [MESSAGE_TYPES.CREATE]: {
    icon: faPlus,
    color: UI_COLORS.STATUS.SUCCESS,
    bgColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.2),
    borderColor: UI_COLORS.STATUS.SUCCESS,
  },
  [MESSAGE_TYPES.UPDATE]: {
    icon: faEdit,
    color: UI_COLORS.STATUS.WARNING,
    bgColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.2),
    borderColor: UI_COLORS.STATUS.WARNING,
  },
  [MESSAGE_TYPES.ERROR]: {
    icon: faTimes,
    color: UI_COLORS.STATUS.ERROR,
    bgColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.2),
    borderColor: UI_COLORS.STATUS.ERROR,
  },
};

/**
 * MessageAlert
 *
 * A transient, animated toast-like alert that appears near the bottom
 * center of the viewport. It supports multiple message types (success,
 * error, create, update, delete) and auto-dismiss behavior.
 *
 * Props:
 * - message: text to display
 * - type: one of MESSAGE_TYPES
 * - duration: milliseconds to stay visible before auto-hide
 * - onClose: callback when the alert finishes closing
 * - isVisible: control visibility from parent/context
 */
const MessageAlert = ({ 
  message, 
  type = MESSAGE_TYPES.SUCCESS, 
  duration = 3000, 
  onClose,
  isVisible = false
}) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [animate, setAnimate] = useState(false);

  // Pick visual configuration for the requested message type.
  const config = TYPE_CONFIG[type] || TYPE_CONFIG[MESSAGE_TYPES.SUCCESS];

  // Show and hide state machine: when `isVisible` becomes true we set show,
  // then trigger animation; after `duration` we animate out and call onClose.
  useEffect(() => {
    if (isVisible && message) {
      setShow(true);
      // Small delay to trigger animation
      const animateTimeout = setTimeout(() => setAnimate(true), 50);
      
      // Auto-hide after duration
      const hideTimeout = setTimeout(() => {
        setAnimate(false);
        // Wait for fade out animation to complete
        setTimeout(() => {
          setShow(false);
          onClose?.();
        }, 300);
      }, duration);

      return () => {
        clearTimeout(animateTimeout);
        clearTimeout(hideTimeout);
      };
    } else {
      setShow(false);
      setAnimate(false);
    }
  }, [isVisible, message, duration, onClose]);

  // Manual close handler used by the close button.
  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      setShow(false);
      onClose?.();
    }, 300);
  };

  if (!show || !message) return null;

  return (
    <div
      className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out ${
        animate ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
      style={{
        minWidth: '320px',
        maxWidth: '600px',
      }}
    >
      <div
        className="flex items-center justify-between p-4 rounded-xl shadow-lg border-l-4"
        style={{
          backgroundColor: config.bgColor,
          borderLeftColor: config.borderColor,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full"
            style={{
              backgroundColor: colorHelpers.withOpacity(config.color, 0.2),
              color: config.color,
            }}
          >
            <FontAwesomeIcon icon={config.icon} className="text-sm" />
          </div>
          <p
            className="font-medium text-sm"
            style={{ color: UI_COLORS.TEXT.PRIMARY }}
          >
            {message}
          </p>
        </div>
        
        <button
          onClick={handleClose}
          className="ml-4 p-1 rounded-full transition-all duration-200 hover:scale-110"
          style={{
            color: UI_COLORS.TEXT.MUTED,
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
          }}
    aria-label={t('common.messageAlert.closeAria')}
        >
          <FontAwesomeIcon icon={faTimes} className="text-xs" />
        </button>
      </div>
    </div>
  );
};

// Context and Provider for managing message alerts globally
const MessageAlertContext = React.createContext();

/**
 * MessageAlertProvider
 *
 * Context provider that exposes helpers to show/hide transient alerts from
 * anywhere in the component tree. It keeps a single alert state and renders
 * a `MessageAlert` instance that is controlled by that state.
 *
 * Exposed context methods:
 * - showMessage(message, type, duration)
 * - showSuccess/showDelete/showCreate/showUpdate/showError convenience wrappers
 * - hideMessage()
 */
export const MessageAlertProvider = ({ children }) => {
  const [alertState, setAlertState] = useState({
    message: '',
    type: MESSAGE_TYPES.SUCCESS,
    isVisible: false,
    duration: 3000,
  });

  /**
   * Show a message with the specified type and duration.
   * This replaces any currently visible message.
   */
  const showMessage = (message, type = MESSAGE_TYPES.SUCCESS, duration = 3000) => {
    setAlertState({
      message,
      type,
      isVisible: true,
      duration,
    });
  };

  /** Hide the currently visible message (triggers exit animation). */
  const hideMessage = () => {
    setAlertState(prev => ({ ...prev, isVisible: false }));
  };

  // Convenience methods for different message types
  const showSuccess = (message, duration) => showMessage(message, MESSAGE_TYPES.SUCCESS, duration);
  const showDelete = (message, duration) => showMessage(message, MESSAGE_TYPES.DELETE, duration);
  const showCreate = (message, duration) => showMessage(message, MESSAGE_TYPES.CREATE, duration);
  const showUpdate = (message, duration) => showMessage(message, MESSAGE_TYPES.UPDATE, duration);
  const showError = (message, duration) => showMessage(message, MESSAGE_TYPES.ERROR, duration);

  const contextValue = {
    showMessage,
    showSuccess,
    showDelete,
    showCreate,
    showUpdate,
    showError,
    hideMessage,
  };

  return (
    <MessageAlertContext.Provider value={contextValue}>
      {children}
      <MessageAlert
        message={alertState.message}
        type={alertState.type}
        isVisible={alertState.isVisible}
        duration={alertState.duration}
        onClose={hideMessage}
      />
    </MessageAlertContext.Provider>
  );
};

// Hook to use message alerts
/**
 * useMessageAlert
 *
 * Hook that returns the message alert context helpers. Throws a helpful
 * error when used outside of `MessageAlertProvider`.
 */
export const useMessageAlert = () => {
  const { t } = useTranslation();
  const context = React.useContext(MessageAlertContext);
  if (!context) {
    throw new Error(t('common.messageAlert.providerError'));
  }
  return context;
};

// Export types for convenience
export { MESSAGE_TYPES };

export default MessageAlert;